import React, { useMemo, useState } from 'react'
import Skeleton from 'react-loading-skeleton'

import { MenuItem as OOMenuItem } from 'src/apollo/onlineOrdering'

import FormattedPrice from 'shared/components/common/price/FormattedPrice'
import { ScreenWidth, useIsMobile } from 'shared/js/utils/WindowContext'
import { t } from 'src/translations'

import { CollapsibleText } from 'public/components/default_template/collapsible_text/CollapsibleText'
import { MenuItem } from 'public/components/default_template/menu_section/MenuSection'
import {
  useItemTags,
  ItemTags
} from 'public/components/default_template/online_ordering/item_tags/ItemTags'
import { useModifierContext } from 'public/components/online_ordering/ModifierContext'
import { useOffers } from 'public/components/online_ordering/OffersContext'
import { ModifierGroup } from 'public/components/online_ordering/types'

import { MenuItemImage } from 'public/components/default_template/product_display/MenuItemImage'
import { ModifiersList } from 'public/components/default_template/product_display/ModifiersList'
import { VomAddToCartSection } from 'public/components/default_template/product_display/view_only_menu_product_display/VomAddToCartSection'
import VomBreadcrumbs from 'public/components/default_template/product_display/view_only_menu_product_display/VomBreadcrumbs'
import { ProductDisplayNestedModifierModal } from 'public/components/default_template/product_display/ProductDisplayNestedModifierModal'

type Props = {
  menuItem?: MenuItem
  modifierGroups: ModifierGroup[]
  setShowFooter: (showFooter: boolean) => void
  loading: boolean
  isOrderable: boolean
}

export const VomProductDisplay = ({
  menuItem,
  modifierGroups,
  setShowFooter,
  loading,
  isOrderable
}: Props) => {
  const { price, quantity, invalidRequiredGroups, modifiers } =
    useModifierContext()
  // Note: invalidRequiredGroups is also used by ModifiersList but we still need it here for price calculation

  // Include default modifier prices in total calculation
  const totalItemPrice = useMemo(() => {
    const basePrice = quantity * price
    const defaultModifiersPrices = menuItem?.defaultModifiersPrice ?? 0
    return basePrice + defaultModifiersPrices
  }, [quantity, price, menuItem?.defaultModifiersPrice])

  // Check if item has required modifiers that haven't been selected yet
  const hasUnselectedRequiredModifiers = useMemo(() => {
    return invalidRequiredGroups.length > 0
  }, [invalidRequiredGroups])

  // Determine what to display for price
  const priceDisplay = useMemo(() => {
    if (isNaN(quantity)) {
      return '0.00'
    }

    // If there are unselected required modifiers and total price is 0, show message instead
    if (hasUnselectedRequiredModifiers && totalItemPrice === 0) {
      return t('product-display.price-calculated-after-modifier-selection')
    }

    return <FormattedPrice value={totalItemPrice} />
  }, [quantity, hasUnselectedRequiredModifiers, totalItemPrice, t])

  const { getOffersTextForEntity } = useOffers()
  const offersText = getOffersTextForEntity(
    menuItem?.guid,
    menuItem?.itemGroupGuid
  )
  // eslint-disable-next-line no-extra-parens
  const itemTags = useItemTags(
    menuItem?.itemTags,
    (menuItem as OOMenuItem)?.contentAdvisories
  )
  const [modifiersInvalid, setModifiersInvalid] = useState(false)

  // Show modal when user has selected a nested modifier (level 2+)
  const showNestedModal = modifiers.length > 1

  const itemImageElement = (
    <>
      {menuItem?.imageUrls && (
        <MenuItemImage item={menuItem} style={{ maxHeight: '400px' }} />
      )}
    </>
  )

  const itemNameElement = (
    <>
      <h2 className='productNameRow' data-testid='productName'>
        {menuItem?.name}
      </h2>
    </>
  )

  const itemDescriptionElement = (
    <>
      <CollapsibleText
        text={menuItem?.description || ''}
        className='productDescription'
      />
    </>
  )

  const itemPriceElement = (
    <>
      {isOrderable && (
        <div className='pricesRow'>
          {/* TODO show offer price */}
          <h4>{priceDisplay}</h4>
          <ItemTags offersText={offersText} itemTags={itemTags} />
        </div>
      )}
    </>
  )

  const itemModsElement = (
    <>
      {isOrderable && (
        <>
          <div className='vomModifiers'>
            <ModifiersList
              groups={modifierGroups}
              modifiersInvalid={modifiersInvalid}
            />
          </div>

          <VomAddToCartSection
            totalItemPrice={totalItemPrice}
            setShowFooter={setShowFooter}
            setModifiersInvalid={setModifiersInvalid}
            outOfStock={menuItem?.outOfStock ?? false}
          />
        </>
      )}
    </>
  )

  const productLeftSection = menuItem?.imageUrls ? (
    <>
      <div className='productLeftSection'>{itemImageElement}</div>
    </>
  ) : (
    <></>
  )

  const productRightSection = (
    <>
      <div className='productRightSection'>
        {itemNameElement}
        {itemPriceElement}
        {itemDescriptionElement}
        {itemModsElement}
      </div>
    </>
  )

  if (loading) {
    return <LoadingSkeleton />
  }

  return (
    <div className='paddedContent'>
      <div
        className='productDisplayBreadcrumbs'
        aria-label={t('product-display.breadcrumbs')}
      >
        <VomBreadcrumbs name={menuItem?.name || ''} />
      </div>
      <div className='productDisplaySection'>
        {productLeftSection}
        {productRightSection}
      </div>

      {showNestedModal && <ProductDisplayNestedModifierModal />}
    </div>
  )
}

const LoadingSkeleton = () => {
  const isMobile = useIsMobile(ScreenWidth.SMALL)

  return (
    <div className='paddedContent'>
      <div
        className='productDisplayBreadcrumbs'
        aria-label={t('product-display.breadcrumbs')}
      >
        <Skeleton width={200} />
      </div>
      <div className='productDisplaySection'>
        <div className='productLeftSection'>
          <Skeleton
            containerClassName='loadingSkeleton'
            height={isMobile ? 300 : 600}
          />
        </div>
        <div className='productRightSection'>
          <h2 className='productNameRow' data-testid='productName'>
            <Skeleton containerTestId='productName-loading-skeleton' />
          </h2>

          <div className='pricesRow'>
            <Skeleton
              width='50%'
              containerClassName='loadingSkeleton'
              containerTestId='price-loading-skeleton'
            />
          </div>

          <div className='modifierGroupRow'>
            <Skeleton count={2} containerTestId='modifiers-loading-skeleton' />
          </div>

          <div className='addToCartRow'>
            <Skeleton containerClassName='loadingSkeleton' height={48} />
            <Skeleton width={100} height={48} />
          </div>

          <Skeleton count={2} containerTestId='description-loading-skeleton' />
        </div>
      </div>
    </div>
  )
}
